import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  FormHelperText,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { executeUploadedSlideIngest } from 'api/uploads';
import { DialogChromeFooter, DialogChromeHeader, dialogPrimaryActionSx } from 'components/atoms/Dialog/DialogChrome';
import { ActionStepper } from 'components/StudyDashboard/ProceduresPage/Actions/ActionStepper';
import { JobNameAndDescriptionStep } from 'components/StudyDashboard/ProceduresPage/Actions/utils';
import { UploadDashboardRow } from 'interfaces/fileList';
import { some } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';

interface UploadIngestExecutionDialogProps {
  row: UploadDashboardRow;
  studyId: string;
  labId: string;
  onClose: () => void;
}

interface UploadIngestExecutionFormValues {
  jobName: string;
  jobDescription: string;
  confirmReingest: boolean;
}

const CONFIRM_REINGEST_STEP_INDEX = 2;

const UploadIngestExecutionDialog = ({ row, studyId, labId, onClose }: UploadIngestExecutionDialogProps) => {
  const queryClient = useQueryClient();
  const requiresReingestConfirmation = row.uploadCategory !== 'awaitingIngest';
  // Determined by the backend: the upload list sets this flag when re-ingesting
  // would collide with an existing slide record.
  const willCreateDuplicateSlide = Boolean(row.willCreateDuplicateOnReingest);
  const [activeStep, setActiveStep] = React.useState(0);
  const [isStepFailed, setIsStepFailed] = React.useState<Record<number, boolean>>({});

  const formMethods = useForm<UploadIngestExecutionFormValues>({
    mode: 'onChange',
    defaultValues: {
      jobName: '',
      jobDescription: '',
      confirmReingest: false,
    },
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    trigger,
    watch,
  } = formMethods;

  const jobName = watch('jobName');
  const confirmReingest = watch('confirmReingest');

  React.useEffect(() => {
    reset({
      jobName: '',
      jobDescription: '',
      confirmReingest: false,
    });
    setActiveStep(0);
    setIsStepFailed(requiresReingestConfirmation ? { [CONFIRM_REINGEST_STEP_INDEX]: true } : {});
  }, [requiresReingestConfirmation, reset, row.id]);

  React.useEffect(() => {
    if (!requiresReingestConfirmation) {
      return;
    }

    setIsStepFailed((currentStepFailures) => ({
      ...currentStepFailures,
      [CONFIRM_REINGEST_STEP_INDEX]: !confirmReingest,
    }));
  }, [confirmReingest, requiresReingestConfirmation]);

  const uploadIngestMutation = useMutation({
    mutationFn: ({ jobName: requestedJobName, jobDescription }: UploadIngestExecutionFormValues) =>
      executeUploadedSlideIngest({
        studyId,
        uploadKeys: row.uploadKey ? [row.uploadKey] : [],
        labId,
        reingest: requiresReingestConfirmation,
        jobName: requestedJobName,
        jobDescription,
      }),
    onSuccess: (response) => {
      enqueueSnackbar(requiresReingestConfirmation ? 'Uploaded slide re-ingest queued.' : response.message, {
        variant: 'success',
      });
      void queryClient.invalidateQueries({
        queryKey: ['studyUploads', studyId, labId],
        exact: true,
      });
      onClose();
    },
    onError: (error: any) => {
      enqueueSnackbar(error?.response?.body?.message || 'Failed to queue uploaded slide ingest.', {
        variant: 'error',
      });
    },
  });

  const stepFieldNames = React.useMemo(
    () => [[], [], requiresReingestConfirmation ? ['confirmReingest'] : []],
    [requiresReingestConfirmation]
  );

  const checkValidationAndSetIsStepFailed = React.useCallback(
    async (stepIndex: number) => {
      const isValid = await trigger(stepFieldNames[stepIndex] as Array<keyof UploadIngestExecutionFormValues>);

      setIsStepFailed((currentStepFailures) => ({
        ...currentStepFailures,
        [stepIndex]: !isValid,
      }));

      return isValid;
    },
    [stepFieldNames, trigger]
  );

  const steps = React.useMemo(() => {
    const jobMetadataStep = {
      label: 'Job name and description',
      subLabel: activeStep !== 0 ? jobName : undefined,
      optional: true,
      content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
    };

    const reviewStep = {
      label: 'Review upload',
      subLabel: row.fileName || row.name,
      content: (
        <Stack spacing={2}>
          <Alert severity={requiresReingestConfirmation ? 'warning' : 'info'}>
            {requiresReingestConfirmation
              ? 'This upload already completed. Confirm that you want to queue a new ingest attempt for the same uploaded slide.'
              : 'Queue ingest for this uploaded slide.'}
          </Alert>
          {willCreateDuplicateSlide ? (
            <Alert severity="warning">
              A <strong>duplicate slide entry</strong> will be created for <strong>{row.fileName || row.name}</strong>{' '}
              because this upload already has an existing slide record. A date-based prefix will be applied
              automatically to distinguish the new slide from the existing one.
            </Alert>
          ) : null}
          <Stack spacing={0.75}>
            <Typography variant="body2">
              <strong>File:</strong> {row.fileName || row.name}
            </Typography>
            <Typography variant="body2">
              <strong>Status:</strong> {row.status || 'Unknown'}
            </Typography>
            <Typography variant="body2">
              <strong>Upload path:</strong> {row.path}
            </Typography>
          </Stack>
        </Stack>
      ),
    };

    if (!requiresReingestConfirmation) {
      return [jobMetadataStep, reviewStep];
    }

    return [
      jobMetadataStep,
      reviewStep,
      {
        label: 'Confirm re-ingest',
        subLabel: confirmReingest ? 'Re-ingest confirmed' : 'Confirmation required',
        content: (
          <Stack spacing={2}>
            <Alert severity="warning">
              Re-ingesting a completed upload should be used only when you explicitly want a new ingest run.
            </Alert>
            <Controller
              name="confirmReingest"
              control={control}
              rules={{
                validate: (value) => value || 'Confirm re-ingest before queuing a new ingest run for this upload.',
              }}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(field.value)}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label="I understand this upload already completed and I want to queue a new ingest run."
                />
              )}
            />
            {errors.confirmReingest?.message ? (
              <FormHelperText error>{String(errors.confirmReingest.message)}</FormHelperText>
            ) : null}
          </Stack>
        ),
      },
    ];
  }, [
    activeStep,
    confirmReingest,
    control,
    errors,
    jobName,
    register,
    requiresReingestConfirmation,
    row,
    willCreateDuplicateSlide,
  ]);

  const handleDialogClose = () => {
    if (uploadIngestMutation.isLoading) {
      return;
    }

    onClose();
  };

  const handleExecute = handleSubmit(async (formValues) => {
    await uploadIngestMutation.mutateAsync(formValues);
  });
  const lastVisibleStepIndex = steps.reduce(
    (currentLastStepIndex, step, stepIndex) =>
      (step as { hidden?: boolean }).hidden ? currentLastStepIndex : stepIndex,
    0
  );
  const isExecuteDisabled = uploadIngestMutation.isLoading || activeStep !== lastVisibleStepIndex || some(isStepFailed);

  return (
    <Dialog open onClose={handleDialogClose} maxWidth="md" fullWidth aria-labelledby="upload-ingest-dialog-title">
      <DialogChromeHeader
        title={requiresReingestConfirmation ? 'Re-ingest Uploaded Slide' : 'Ingest Uploaded Slide'}
        subtitle="Use the uploads dashboard execution flow to queue ingest for this uploaded slide."
        titleId="upload-ingest-dialog-title"
        closeButtonAriaLabel="Close upload ingest dialog"
        onClose={handleDialogClose}
        sticky
      />
      <DialogContent dividers>
        <ActionStepper
          activeStateHandlers={[activeStep, setActiveStep]}
          hideExecution
          handleSubmit={handleExecute}
          isStepFailed={isStepFailed}
          setActiveStepForValidation={setActiveStep}
          steps={steps}
          validateStep={() => checkValidationAndSetIsStepFailed(activeStep)}
        />
      </DialogContent>
      <DialogChromeFooter sticky>
        <Button onClick={handleDialogClose} disabled={uploadIngestMutation.isLoading} color="secondary">
          Cancel
        </Button>
        <Button variant="contained" disabled={isExecuteDisabled} onClick={handleExecute} sx={dialogPrimaryActionSx}>
          {uploadIngestMutation.isLoading ? 'Executing...' : 'Execute'}
        </Button>
      </DialogChromeFooter>
    </Dialog>
  );
};

export default UploadIngestExecutionDialog;
